/**
 * Old hash routes ↔ /app paths (plan 4 §4.1). Pure: no DOM.
 * Settings sections that shared one hash in the old app collapse back to it.
 */

const SECTION_LABELS = {
  team: 'Team & access',
  products: 'Products',
  models: 'AI models',
  billing: 'Billing',
  support: 'Support',
  org: 'Organisation',
  organization: 'Organisation',
  connectors: 'Connectors',
  knowledge: 'Knowledge base',
  organizations: 'Organizations',
  notifications: 'Notifications',
  audit: 'Audit log',
  'audit-logs': 'Audit log',
  activity: 'User activity',
  'user-activity': 'User activity',
};

/** @param {string} section */
export function sectionLabel(section) {
  return SECTION_LABELS[section] || 'Settings';
}

/** @param {string} path @param {string} productId */
export function withProduct(path, productId) {
  if (!productId) return path;
  const [pathname, query = ''] = String(path).split('?');
  const params = new URLSearchParams(query);
  params.set('product_id', productId);
  const qs = params.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

function decodePart(value) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/**
 * @param {string} hash Hash with or without a leading '#'.
 * @returns {string} Path beginning with /app
 */
export function appPathFromHash(hash) {
  const raw = String(hash || '').replace(/^#/, '');
  const qIndex = raw.indexOf('?');
  const name = (qIndex === -1 ? raw : raw.slice(0, qIndex)) || 'dashboard';
  const params = new URLSearchParams(qIndex === -1 ? '' : raw.slice(qIndex + 1));
  const productId = params.get('product_id') || '';
  let path = '/app/dashboard';
  if (name === 'chat') {
    const group = params.get('group');
    path = group ? `/app/chat/${encodeURIComponent(group)}` : '/app/chat';
  } else if (name === 'knowledge-base' || name === 'context') path = '/app/knowledge';
  else if (name === 'connectors') path = '/app/connectors';
  else if (name === 'schedules') path = '/app/schedules';
  else if (name === 'settings') path = `/app/settings/${encodeURIComponent(params.get('section') || 'team')}`;
  else if (name === 'models') path = '/app/settings/models';
  else if (name === 'add-product') path = '/app/products/new';
  else if (name === 'organizations') path = '/app/settings/organizations';
  else if (name === 'audit-logs') path = '/app/settings/audit';
  else if (name === 'user-activity') path = '/app/settings/activity';
  else if (name === 'product' || name === 'product-context') {
    const id = params.get('id') || params.get('product') || '';
    path = id ? `/app/products/${encodeURIComponent(id)}` : '/app/settings/products';
  }
  else if (name === 'login') path = '/app/login';
  else if (name === 'signup') path = '/app/signup';
  else if (name === 'accept-invite') path = '/app/accept-invite';
  else if (name === 'offline') path = '/app/offline';
  else if (name === 'dashboard' || name === 'first-read' || name === 'post-login') path = '/app/dashboard';
  if (path === '/app/offline') return path;
  if (path === '/app/login' || path === '/app/signup' || path === '/app/accept-invite') {
    return path + authQueryString(params);
  }
  return withProduct(path, productId);
}

/** @param {URLSearchParams} params */
function authQueryString(params) {
  const next = new URLSearchParams();
  for (const key of ['oauth', 'token', 'email']) {
    const value = params.get(key);
    if (value != null && value !== '') next.set(key, value);
  }
  const qs = next.toString();
  return qs ? `?${qs}` : '';
}

/**
 * @param {string} pathname
 * @param {string} [search]
 * @returns {string} Hash beginning with '#'
 */
export function hashFromAppPath(pathname, search) {
  const params = new URLSearchParams(String(search || '').replace(/^\?/, ''));
  const productId = params.get('product_id') || '';
  const parts = String(pathname || '').split('?')[0].replace(/\/+$/, '').split('/').filter(Boolean);
  const head = parts[1] || '';
  const extra = parts[2] ? decodePart(parts[2]) : '';
  let hash = '#dashboard';
  if (head === 'chat') hash = extra && extra !== 'new' ? `#chat?group=${encodeURIComponent(extra)}` : '#chat';
  else if (head === 'knowledge') hash = '#knowledge-base';
  else if (head === 'connectors') hash = '#connectors';
  else if (head === 'schedules') hash = '#schedules';
  else if (head === 'organizations') hash = '#organizations';
  else if (head === 'products' && extra === 'new') hash = '#add-product';
  else if (head === 'products' && extra) {
    hash = `#product?id=${encodeURIComponent(extra)}`;
  }
  else if (head === 'offline') hash = '#offline';
  else if (head === 'login') hash = '#login';
  else if (head === 'signup') hash = '#signup';
  else if (head === 'accept-invite') hash = '#accept-invite';
  else if (head === 'settings') hash = settingsHash(extra || 'team', parts[3] ? decodePart(parts[3]) : '');
  if (head === 'login' || head === 'signup' || head === 'accept-invite') {
    return hash + authQueryString(params);
  }
  if (!productId || hash === '#offline') return hash;
  return `${hash}${hash.includes('?') ? '&' : '?'}product_id=${encodeURIComponent(productId)}`;
}

function settingsHash(section, modelId) {
  if (section === 'connectors') return '#connectors';
  if (section === 'knowledge') return '#knowledge-base';
  if (section === 'organizations') return '#organizations';
  if (section === 'models' && modelId) {
    return `#models?model=${encodeURIComponent(modelId)}`;
  }
  if (section === 'models') return '#models';
  if (section === 'products' || section === 'billing' || section === 'support'
    || section === 'org' || section === 'organization'
    || section === 'notifications' || section === 'audit' || section === 'activity') {
    return `#settings?section=${encodeURIComponent(section)}`;
  }
  return '#settings';
}

/**
 * Legacy `/app#…` or `/classic#…` (after the server redirect) → `/app/…`.
 * Real `/app/…` paths have no hash and stay put.
 * @param {{ hash?: string }} loc
 * @returns {string|null}
 */
export function legacyHashAppUrl(loc) {
  const hash = loc && loc.hash ? String(loc.hash) : '';
  if (!hash) return null;
  return appPathFromHash(hash);
}
